import { useState } from "react";
import { toast } from "sonner";
import { Phone, IdCard } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fancallWhen } from "@/lib/api";

const YesNo = ({ value, onPick, testPrefix, disabled, lockYes }) => (
  <div className="grid grid-cols-2 gap-2">
    {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map((o) => (
      <button
        key={o.l}
        type="button"
        disabled={disabled || (lockYes && o.v)}
        data-testid={`${testPrefix}-${o.l.toLowerCase()}`}
        onClick={() => onPick(o.v)}
        className={`h-10 rounded-xl border text-sm font-semibold transition-colors duration-200 disabled:opacity-40 ${value === o.v ? (o.v ? "border-emerald-500 bg-emerald-500/15 text-emerald-200" : "border-zinc-500 bg-zinc-700/40 text-zinc-100") : "border-zinc-700 hover:border-zinc-500"}`}
      >
        {o.l}
      </button>
    ))}
  </div>
);

export { YesNo };

export const WinnerLockNote = () => (
  <div data-testid="fancall-winner-lock-note" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-200">You will be able to join the raffle again at reset</div>
);

export default function FancallPanel({ drop, onChanged }) {
  const [jid, setJid] = useState("");
  const current = drop.fancall_optins.find((o) => o.joiner_id === jid);
  const lockYes = !!jid && drop.fancall_winners?.includes(jid);
  const value = lockYes ? false : current?.opt_in;
  const isUnit = drop.fill_rule === "unit";
  const yes = drop.fancall_optins.filter((o) => o.opt_in === true);

  const answer = async (opt_in) => {
    try {
      await api.put(`/drops/${drop.id}/fancall-optin`, { joiner_id: jid, opt_in });
      toast.success(opt_in ? "You're opted into the fancall" : "Opted out of the fancall");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.04] p-5 sm:p-6" data-testid="fancall-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-amber-500/15 text-amber-300 grid place-items-center"><Phone className="h-5 w-5" /></span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-bold">Fancall Round</div>
          <div className="text-sm text-zinc-400" data-testid="fancall-datetime">{fancallWhen(drop) || (isUnit ? "Event date to be announced" : "Fancall date & time to be announced")}</div>
        </div>
        {drop.passport_only && (
          <span data-testid="passport-only-notice" className="inline-flex items-center gap-1.5 rounded-full border border-pink-500/40 bg-pink-500/10 px-3 py-1 text-xs font-bold tracking-wider uppercase text-pink-200">
            <IdCard className="h-4 w-4" /> Passport Only
          </span>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <div className="space-y-2">
          <div className="text-sm font-semibold">{isUnit ? "Do you wish to opt in for the call?" : "Do you wish to opt into the fancall?"}</div>
          {isUnit && <div data-testid="fancall-entry-rule" className="text-xs text-zinc-400">If Yes, each claim is 1 entry for the fancall raffle, up to 4 entries max.</div>}
          <Select value={jid} onValueChange={setJid}>
            <SelectTrigger data-testid="fancall-joiner-select" className="bg-zinc-950"><SelectValue placeholder={drop.fancall_optins.length ? "Select your name" : "Claim a member first"} /></SelectTrigger>
            <SelectContent>{drop.fancall_optins.map((o) => <SelectItem key={o.joiner_id} value={o.joiner_id}>{o.joiner_name}</SelectItem>)}</SelectContent>
          </Select>
          {lockYes ? <WinnerLockNote /> : <YesNo value={value} onPick={answer} testPrefix="fancall-optin" disabled={!jid} />}
        </div>
        <div>
          <div className="text-sm font-semibold">Opted in <span className="font-mono text-zinc-400">({yes.length})</span></div>
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="fancall-optin-list">
            {yes.length === 0 && <span className="text-sm text-zinc-500">No one yet</span>}
            {yes.map((o) => <span key={o.joiner_id} className="rounded-md bg-emerald-500/10 text-emerald-200 px-2 py-0.5 text-xs">{o.joiner_name}</span>)}
          </div>
        </div>
      </div>
    </div>
  );
}
